import {GridColDef} from '@mui/x-data-grid';
import {omit} from 'lodash';
import {FC, useCallback, useMemo, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {useStore} from 'zustand';
import {DashboardDtoPaged} from '../../../../../../global/api';
import {Table, TableProps} from '../../../../../Table';
import {
  buildServiceRequestsDashboardRows,
  getContractStatusByRequests,
} from '../../helpers/buildServiceRequestsDashboardRows';
import {getServiceRequestsDashboardColumns} from '../../constants/service-request-dashboard-columns';
import {useServiceRequestsGroupedColumns} from '../../hooks/useServiceRequestsGroupedColumns';
import {useStoresContext} from '../../hooks/useStoresContext';
import {fullFilter} from '../../store/useServiceRequestsStatusFilterStore';
import {ServiceRequestDashboardRow} from '../../types/service-requests-dashboard.type';

interface Props extends Omit<TableProps, 'columns'> {
  rows: DashboardDtoPaged[];
  serviceType: string;
}

export const ServiceRequestsDashboardTable: FC<Props> = ({rows, serviceType, ...tableProps}) => {
  const {pathname} = useLocation();
  const allRows: ServiceRequestDashboardRow[] = useMemo(() => buildServiceRequestsDashboardRows(rows), [rows]);
  const {serviceRequestsStatusFilterStore} = useStoresContext(serviceType);
  const {statusFilter} = useStore(serviceRequestsStatusFilterStore);

  const [openByContractNumber, setOpenByContractNumber] = useState<Record<string, boolean>>({});

  const toggle = useCallback((contractNumber: string) => {
    setOpenByContractNumber((prev) => ({
      ...prev,
      [contractNumber]: !prev[contractNumber],
    }));
  }, []);

  const handleRowClick = useCallback(
    (requestId?: string) => {
      if (!requestId) return;
      const url = `/serviceRequests/${requestId}`;
      sessionStorage.setItem('current', url);
      const newWindow = window.open(url, '_blank');
      newWindow?.addEventListener('load', () => sessionStorage.setItem('current', pathname), {once: true});
    },
    [pathname],
  );

  const baseColumns = useMemo(
    () =>
      getServiceRequestsDashboardColumns({
        onRequestClick: handleRowClick,
        openByContractNumber,
      }),
    [handleRowClick, openByContractNumber],
  );
  const columns = useServiceRequestsGroupedColumns(baseColumns, openByContractNumber, toggle);

  const getColumnsManagementFields = useCallback(
    (cols: GridColDef[]) => cols.filter((col) => col.field !== '__expander__').map((col) => col.field),
    [],
  );

  const visibleRows = useMemo(() => {
    const statusSet = new Set(statusFilter);
    const isStatusFilterActive = statusSet.size < fullFilter.length;

    return allRows
      .map((row) => {
        const serviceRequests = isStatusFilterActive
          ? row.serviceRequests.filter((request) => Boolean(request.statusByDate && statusSet.has(request.statusByDate)))
          : row.serviceRequests;

        return {
          ...row,
          serviceRequests,
          statusByDate: serviceRequests.length ? getContractStatusByRequests(serviceRequests) : row.statusByDate,
          archived: serviceRequests.length ? serviceRequests.some((request) => request.archived) : row.archived,
        };
      })
      .filter((row) => !isStatusFilterActive || row.serviceRequests.length > 0);
  }, [allRows, statusFilter]);

  return (
    <Table
      tableContainerSx={{px: 0}}
      {...omit(tableProps, ['totalCount'])}
      columns={columns}
      rows={visibleRows}
      columnsManagementGetTogglableColumns={getColumnsManagementFields}
      getRowHeight={() => 'auto'}
      sx={{
        '& .MuiDataGrid-cell': {
          py: 0,
          alignItems: 'flex-start',
        },
      }}
      pageSizeTitle="Контрактов на странице:"
    />
  );
};
